import { useMemo } from 'react';
import {
  MOBILE_TRUNCATED_ACCESS_REQUESTS_PAGE_SIZE,
  TRUNCATED_ACCESS_REQUESTS_PAGE_SIZE,
} from '@frameio/access-requests/constants/';
import { useEvictedAccessRequests } from '@frameio/access-requests/contexts/EvictedAccessRequestsContext';
import type { AccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import { useAccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import { useDeeplinkedAccessRequestIdFromParams } from '@frameio/access-requests/hooks/useDeeplinkedAccessRequestIdFromParams';
import { useFilteredAccessRequests } from '@frameio/access-requests/hooks/useFilteredAccessRequests';
import { number } from '@frameio/i18n';
import { useActiveWindowBreakpoint } from '@frameio/vapor';
import { useShouldShowTruncatedAccessRequestsList } from '../../../contexts/ShouldShowTruncatedAccessRequestsList';
import type { useNotificationGroups } from '../../../hooks/useNotificationGroups';
import type { GroupedNotificationItems } from '../../NotificationCell/types';
import TEXT from './generated/TEXT/AccessRequestsGroup';

TEXT.DEFINE({
  /** Label for access requests group within the notifications panel */
  groupLabel: (total: number): string => `Access Requests (${number(total)})`,
});

type UseAccessRequestsGroupProps = {
  data: ReturnType<typeof useNotificationGroups>['data'];
};

type UseAccessRequestsGroupReturn = {
  groupedAccessRequests: GroupedNotificationItems | undefined;
  selectedAccessRequestId: string | null;
};

/**
 * @description The `useAccessRequestsGroup` hook processes and groups access requests for display
 * in the notifications list. It handles deeplinked access requests from URL parameters, watches
 * for cache evictions, and manages the display order and selection state of access requests.
 *
 * Key features:
 * - Displays a deeplinked access request at the top of the list when present in URL params
 * - Watches the Apollo cache to automatically remove the deeplinked node when it's been evicted
 * - Filters out duplicate deeplinked requests from the remaining paginated results
 * - Manages the display of truncated access requests lists, with a "Show All" button

 *
 * @param {UseAccessRequestsGroupProps} params - The hook parameters.
 * @param {ReturnType<typeof useNotificationGroups>['data']} params.data - The data containing access requests.
 *
 * @returns {UseAccessRequestsGroupReturn} An object containing the grouped access requests and
 * selected ID.
 * @property {GroupedNotificationItems | undefined} groupedAccessRequests - The grouped access
 * requests with label and items, or undefined if no access requests exist.
 * @property {string | null} selectedAccessRequestId - The ID of the access request that should
 * be selected in the notifications list, or null if no valid deeplinked request exists.
 *
 * @example
 * const { groupedAccessRequests, selectedAccessRequestId } = useAccessRequestsGroup({
 *   data: notificationGroupsData,
 * });
 */
export const useAccessRequestsGroup = ({
  data,
}: UseAccessRequestsGroupProps): UseAccessRequestsGroupReturn => {
  const { deeplinkedAccessRequestId, accessRequestData } =
    useDeeplinkedAccessRequestIdFromParams();
  const breakpoint = useActiveWindowBreakpoint();

  const {
    filteredAccessRequests: allAccessRequests,
    filteredCount: totalAccessRequestsCount,
  } = useFilteredAccessRequests({
    accessRequestNodes: data?.account?.accessRequests?.nodes ?? [],
    total: data?.account?.accessRequests?.totalCount ?? 0,
  });

  const { evictedAccessRequests } = useEvictedAccessRequests();
  const { shouldShowTruncatedAccessRequestsList } =
    useShouldShowTruncatedAccessRequestsList();

  /* Watch the deeplinked access request in the cache and detect when it's evicted
   * so it can be removed from the list */
  const { complete } = useAccessRequestNodeFragment({
    from: {
      __typename: 'AccessRequest',
      id: deeplinkedAccessRequestId ?? '',
    },
  });

  /* Check if the deeplinked request has been evicted (optimistically removed pending undo) */
  const isDeeplinkedRequestEvicted =
    deeplinkedAccessRequestId &&
    evictedAccessRequests.some(
      (evicted) => evicted.accessRequest.id === deeplinkedAccessRequestId
    );

  /* If the fragment is incomplete (evicted) or ID is invalid, treat as no deeplinked request */
  const isDeeplinkedRequestValid =
    complete &&
    deeplinkedAccessRequestId &&
    deeplinkedAccessRequestId !== 'invalid' &&
    accessRequestData &&
    !isDeeplinkedRequestEvicted;

  const maxItems =
    breakpoint === 'base'
      ? MOBILE_TRUNCATED_ACCESS_REQUESTS_PAGE_SIZE
      : TRUNCATED_ACCESS_REQUESTS_PAGE_SIZE;
  const shouldTruncateList =
    shouldShowTruncatedAccessRequestsList &&
    totalAccessRequestsCount > maxItems;

  const seeAllAccessRequestsButton = useMemo(() => {
    return {
      id: 'show-all-access-requests',
    };
  }, []);

  const accessRequestsCountLabel = TEXT.groupLabel(totalAccessRequestsCount);

  const groupedAccessRequests = useMemo(():
    | GroupedNotificationItems
    | undefined => {
    if (totalAccessRequestsCount === 0) {
      return undefined;
    }
    let items: AccessRequestNodeFragment[] = [];
    if (isDeeplinkedRequestValid && accessRequestData) {
      const deepLinkedNode = accessRequestData;
      const listWithoutDeepLinkedNode = allAccessRequests.filter(
        (ar: AccessRequestNodeFragment) => ar.id !== deeplinkedAccessRequestId
      );
      items = [deepLinkedNode, ...listWithoutDeepLinkedNode];
    } else {
      items = [...allAccessRequests];
    }

    if (shouldTruncateList) {
      const truncatedItems = items.slice(0, maxItems);

      // Inject the "Show All" button as the last item in the list
      truncatedItems.push(seeAllAccessRequestsButton);

      return {
        label: accessRequestsCountLabel,
        id: 'access-requests',
        items: truncatedItems,
      };
    }
    return {
      label: accessRequestsCountLabel,
      id: 'access-requests',
      items,
    };
  }, [
    isDeeplinkedRequestValid,
    deeplinkedAccessRequestId,
    accessRequestData,
    allAccessRequests,
    totalAccessRequestsCount,
    seeAllAccessRequestsButton,
    shouldTruncateList,
    maxItems,
    accessRequestsCountLabel,
  ]);
  return {
    groupedAccessRequests,
    selectedAccessRequestId:
      isDeeplinkedRequestValid && deeplinkedAccessRequestId
        ? deeplinkedAccessRequestId
        : null,
  };
};
